import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarRange, GitCompareArrows } from "lucide-react";
import clsx from "clsx";
import { useDateRange, PRESETS, type Preset } from "../lib/dateRange";

export default function DateRangeFilter() {
  const { preset, start, end, compare, setPreset, setCustom, toggleCompare } = useDateRange();
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(start);
  const [e, setE] = useState(end);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function openCustom() {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      // Fixed-position, viewport-clamped so the 288px panel never spills off-screen.
      const left = Math.min(rect.left, window.innerWidth - 288 - 12);
      setPos({ top: rect.bottom + 6, left: Math.max(12, left) });
    }
    setS(start);
    setE(end);
    setOpen(true);
  }

  function choose(p: Preset) {
    if (p === "custom") {
      open ? setOpen(false) : openCustom();
      return;
    }
    setPreset(p);
    setOpen(false);
  }

  // Close on outside click / Escape / scroll / resize (the popover is fixed-positioned).
  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      const t = ev.target as Node;
      if (popRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") setOpen(false);
    }
    function dismiss() {
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", dismiss);
    window.addEventListener("scroll", dismiss, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("scroll", dismiss, true);
    };
  }, [open]);

  return (
    <div className="relative flex min-w-0 items-center gap-1" ref={anchorRef}>
      <CalendarRange size={14} className="hidden shrink-0 text-slatey sm:block" />
      <div className="flex max-w-full overflow-x-auto rounded-lg border border-white/10">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => choose(p.key)}
            className={clsx(
              "shrink-0 whitespace-nowrap px-2 py-1 text-[11px] transition",
              preset === p.key
                ? "bg-aurora-cyan/15 text-aurora-cyan"
                : "bg-white/5 text-slatey hover:text-ink"
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
      <button
        onClick={toggleCompare}
        title="Compare to previous period"
        className={clsx(
          "flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1 text-[11px] transition",
          compare
            ? "border-pacific/50 bg-pacific/15 text-pacific"
            : "border-white/10 bg-white/5 text-slatey hover:text-ink"
        )}
      >
        <GitCompareArrows size={13} /> <span className="hidden sm:inline">Compare</span>
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={popRef}
            style={{ position: "fixed", top: pos.top, left: pos.left }}
            className="z-[100] w-72 rounded-xl border border-white/10 bg-ocean-900 p-3 shadow-2xl ring-1 ring-black/40"
          >
            <div className="section-tag mb-2">CUSTOM RANGE</div>
            <label className="mb-1 block text-[10px] text-slatey">Start</label>
            <input
              type="datetime-local"
              value={s}
              onChange={(ev) => setS(ev.target.value)}
              className="mb-2 w-full rounded border border-white/10 bg-white/5 px-2 py-1 text-xs"
            />
            <label className="mb-1 block text-[10px] text-slatey">End</label>
            <input
              type="datetime-local"
              value={e}
              onChange={(ev) => setE(ev.target.value)}
              className="mb-3 w-full rounded border border-white/10 bg-white/5 px-2 py-1 text-xs"
            />
            <button
              onClick={() => {
                if (s && e) {
                  setCustom(s, e);
                  setOpen(false);
                }
              }}
              className="w-full rounded-lg bg-pacific/90 py-1.5 text-xs font-medium text-white hover:bg-pacific"
            >
              Apply range
            </button>
            <p className="mt-2 text-[10px] text-slate-600">
              Tip: data spans Dec 2025 – Jun 2026. Presets anchor to the latest exception.
            </p>
          </div>,
          document.body
        )}
    </div>
  );
}
